// Helpers for handling blog post markdown safely.

// Authors sometimes paste markdown tables in a mangled form: rows flattened
// onto one line with `||` separators, header wrapped across two lines, blank
// lines between rows, or rows indented as code blocks. This reconstructs a
// well-formed markdown table so `marked` can parse it. It also converts
// checkbox-glyph checklists into semantic lists (see normalizeChecklists).
export function normalizeMarkdown(md) {
  if (!md) return md;

  // 1. Split flattened rows: any line with many pipes that contains `||`
  //    almost certainly is a table whose newlines got stripped.
  let lines = md.split("\n").flatMap((line) => {
    const pipeCount = (line.match(/\|/g) || []).length;
    if (pipeCount >= 6 && /\|\s*\|/.test(line)) {
      return line.replace(/\|\s*\|/g, "|\n|").split("\n");
    }
    return [line];
  });

  // 2. Dedent every line that starts with `|` so it isn't treated as an
  //    indented code block.
  lines = lines.map((line) => {
    const trimmed = line.replace(/^[ \t]+/, "");
    return trimmed.startsWith("|") ? trimmed : line;
  });

  // 3. Drop blank lines that sit between consecutive `|` lines so `marked`
  //    sees one contiguous table block.
  const collapsed = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() === "") {
      const prev = (collapsed[collapsed.length - 1] || "").trim();
      let nextIdx = i + 1;
      while (nextIdx < lines.length && lines[nextIdx].trim() === "") nextIdx++;
      const next = nextIdx < lines.length ? lines[nextIdx].trim() : "";
      if (prev.startsWith("|") && next.startsWith("|")) continue;
    }
    collapsed.push(lines[i]);
  }

  // 4. Merge wrapped table rows. A row that starts with `|` but doesn't end
  //    with `|` is incomplete — pull in following `|` lines until the row
  //    terminates with `|`.
  const merged = [];
  for (let i = 0; i < collapsed.length; i++) {
    let line = collapsed[i];
    if (line.startsWith("|") && !/\|\s*$/.test(line)) {
      while (
        !/\|\s*$/.test(line) &&
        i + 1 < collapsed.length &&
        collapsed[i + 1].startsWith("|")
      ) {
        line = line.trimEnd() + " " + collapsed[i + 1].trimStart();
        i++;
      }
    }
    merged.push(line);
  }

  return normalizeChecklists(merged.join("\n"));
}

// Authors write "save this" checklists as lines that each start with a ballot
// box glyph (☐ / ☑) or a task-list marker (`[ ]` / `[x]`). Those glyphs are not
// markdown list markers, so `marked` collapses the consecutive lines into a
// single paragraph — every item runs together on one line. This converts each
// run of such lines into a real <ul class="roof-checklist"> so items stack and
// are styled as a checklist (see .roof-checklist in custom.scss).
export function normalizeChecklists(md) {
  if (!md) return md;

  // A checklist line: optional indent, a checkbox glyph or [ ]/[x] marker,
  // whitespace, then the item text (which must be non-empty).
  const CHECK = /^\s*(?:[☐☑☒✅✔✓]|\[[ xX]?\])\s+(.+?)\s*$/;
  // Which glyphs/markers mean the item is already checked off.
  const CHECKED = /^\s*(?:[☑☒✅✔✓]|\[[xX]\])\s+/;

  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    if (!CHECK.test(lines[i])) {
      out.push(lines[i]);
      i++;
      continue;
    }
    // Consume the maximal run of consecutive checklist lines.
    const items = [];
    while (i < lines.length && CHECK.test(lines[i])) {
      const text = lines[i].replace(CHECK, "$1");
      const checked = CHECKED.test(lines[i]);
      items.push(`<li${checked ? ' class="is-checked"' : ""}>${text}</li>`);
      i++;
    }
    // Surround the raw-HTML block with blank lines so `marked` treats it as an
    // HTML block rather than folding it into an adjacent paragraph.
    if (out.length && out[out.length - 1].trim() !== "") out.push("");
    out.push('<ul class="roof-checklist">', ...items, "</ul>", "");
  }

  return out.join("\n");
}

// Strip common markdown markup so a body excerpt reads as plain prose
// instead of leaking raw syntax (pipes, asterisks, hashes, etc.) onto a
// blog index card.
export function stripMarkdown(md) {
  if (!md) return "";
  return md
    .replace(/^\s*\|.*$/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/\n{2,}/g, "\n\n")
    .trim();
}

// Return a short plain-text excerpt suitable for a card. Accepts either
// HTML or markdown content.
export function bodyExcerpt(body, maxLength) {
  if (!body) return "";
  const isHtml = /<[a-z][\s\S]*>/i.test(body);
  let text;
  if (isHtml) {
    const paragraph = /<p[^>]*>([\s\S]*?)<\/p>/i.exec(body);
    text = (paragraph ? paragraph[1] : body).replace(/<[^>]+>/g, "");
  } else {
    text = stripMarkdown(body).split(/\n{2,}/).find((p) => p.trim()) || "";
  }
  text = text.replace(/\s+/g, " ").trim();
  if (maxLength && text.length > maxLength) {
    return text.slice(0, maxLength).replace(/\s+\S*$/, "") + "…";
  }
  return text;
}
